import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { serverSideConfigs, t } from 'localization'
import { useTranslation } from 'next-i18next'
import getConfig from 'next/config'
import { Trans } from 'react-i18next'
import { createMosaicClient } from 'server/utils/mosaicClient'
import Button from 'shared/components/Button'
import { FCLink } from 'shared/components/Links'
import StaticPage from 'shared/components/StaticPage'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { PagesQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { fonts, media } from 'shared/lib'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import styled from 'styled-components'

const TP = 'pages.consign'

type ConsignProps = {
  staticImages: { [key: string]: string }
}

const Consign = ({ staticImages }: ConsignProps) => {
  const isAliasSellingPageEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ALIAS_SELLING_PAGE)
  const isMarketPricing = useFeatureFlag(FeatureFlag.MARKET_PRICING)
  const tv = useTranslation()
  const { country } = useShoppingRegionContext()
  const isJP = country === 'JP'

  return (
    <>
      {isJP || isAliasSellingPageEnabled ? (
        <StaticPage
          qaAttr={PagesQA.SellWithFlightclub}
          title={t(`${TP}.sellWithTitleJP`, 'Start Selling')}
          heroImagePath={staticImages['consign'] || ''}
        >
          {isJP && (
            <>
              <StaticPage.Paragraph>
                <Trans i18nKey={`sellingWithFlightAliasJP1`} t={tv.t}>
                  The easiest way to move your new and lightly worn sneakers is with Flight Club's
                  dedicated selling app, alias.
                </Trans>
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                <Trans i18nKey={`sellingWithFlightAliasJP2`} t={tv.t}>
                  With alias, you have access to AI-driven pricing recommendations, in-depth market
                  insights and more to help you easily price, list and sell your items. Conveniently
                  drop off your items at Flight Club Tokyo for consignment or ship your items for
                  verification once sold. Following verification, you can conveniently manage each
                  of your listings through the alias app.
                </Trans>
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                <Trans i18nKey={`clickBelow`} t={tv.t}>
                  Click the link below to download the alias app, create your account and start
                  selling.
                </Trans>
              </StaticPage.Paragraph>
              <ButtonWrapper>
                <ButtonLink
                  buttonType="primary1"
                  component="a"
                  onClick={() => {
                    window.open('https://www.alias.org/app ', '_blank', 'noopener')
                  }}
                  qaAttr={PagesQA.ConsignAliasDownloadLink}
                >
                  {t(`${TP}.downloadNow`, 'Download Now')}
                </ButtonLink>
              </ButtonWrapper>
            </>
          )}
          {!isJP && (
            <>
              <StaticPage.Paragraph>
                <Trans i18nKey="sellWithAlias1" t={tv.t}>
                  Already a Flight Club seller?{' '}
                  <FCLink
                    href="https://sell.flightclub.com/login"
                    isExternal
                    newTab
                    qaAttr={PagesQA.ConsignLogInSellDotAccountLink}
                  >
                    Log in
                  </FCLink>{' '}
                  now to manage your listings.
                </Trans>
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                {t(
                  `${TP}.sellWithAlias2`,
                  `Since 2005, Flight Club has been the one-stop shop for sneaker enthusiasts looking
                  to buy and sell the rarest shoes, latest releases and coveted classics. With a
                  network of retail locations in New York, Los Angeles, Miami and Tokyo, in addition
                  to a premier online experience, Flight Club is the quickest place to move your new
                  and lightly worn sneakers.`,
                )}
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                {t(
                  `${TP}.sellWithAlias3`,
                  `When you sell with Flight Club, your sneakers are listed for purchase across our
                  retail stores and online. You can also conveniently drop off your sneakers at any
                  of our locations for consignment or ship them for verification once sold.`,
                )}
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                {t(
                  `${TP}.sellWithAlias4`,
                  `As we continue to transform sneaker retail, we’ve created alias, an app
                  specifically designed to improve the seller experience. Click the link below to
                  learn more.`,
                )}
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                <FCLink
                  href="https://www.alias.org/app "
                  isExternal
                  newTab
                  qaAttr={PagesQA.ConsignAliasDownloadLink}
                >
                  {t(`${TP}.sellWithAlias5`, `LEARN MORE`)}
                </FCLink>
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                {t(
                  `${TP}.sellWithAlias6`,
                  `New sellers can create an account on alias as Flight Club no longer supports new
                  seller registrations.`,
                )}
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                <FCLink
                  href="https://support.flightclub.com/hc/en-us/requests/new"
                  isExternal
                  newTab
                  qaAttr={PagesQA.ConsignSellerSupportLink}
                >
                  {t(`${TP}.sellWithAlias7`, `Flight Club Seller Support`)}
                </FCLink>
              </StaticPage.Paragraph>
              <StaticPage.Paragraph>
                <FCLink
                  href="https://www.flightclub.com/seller-terms"
                  newTab
                  qaAttr={PagesQA.ConsignTermsConditionsLink}
                >
                  {t(`${TP}.sellWithAlias8`, `Seller Terms of Use`)}
                </FCLink>
              </StaticPage.Paragraph>
            </>
          )}
        </StaticPage>
      ) : (
        <StaticPage
          qaAttr={PagesQA.SellWithFlightclub}
          title={t(`${TP}.sellWithTitle`, 'Sell with Flight Club')}
          heroImagePath={staticImages['consign'] || ''}
        >
          {isMarketPricing ? (
            <StaticPage.Paragraph>
              <Trans i18nKey={`${TP}.sellingWithFlight`} t={tv.t}>
                Selling with Flight Club is the easiest way to move your new and lightly worn
                sneakers. With our network of retail stores in New York, Los Angeles and Miami, in
                addition to a premier online experience, Flight Club offers the best liquidity and
                sell-through in the game, at the most competitive prices available anywhere.
              </Trans>
            </StaticPage.Paragraph>
          ) : (
            <StaticPage.Paragraph>
              <Trans i18nKey={`${TP}.sellingNotMarketPricing`} t={tv.t}>
                Selling with Flight Club is the easiest way to move your new and lightly worn
                sneakers for money, with little effort on your end. With our network of marketplace
                channels, Flight Club offers the best liquidity and sell-thru in the game, at the
                most competitive prices available anywhere.
              </Trans>
            </StaticPage.Paragraph>
          )}
          <StaticPage.Paragraph>
            <Trans
              i18nKey={`${TP}.readyToSell`}
              t={tv.t}
              defaults="Ready to sell with Flight Club? <0>Create Your Account Here</0>"
              components={[
                <InlineStyledLink
                  href="https://sell.flightclub.com/register"
                  rel="noopener noreferrer"
                  target="_blank"
                  data-qa={PagesQA.ConsignCreateYouAccountHereLink}
                />,
              ]}
            />
          </StaticPage.Paragraph>
          <StaticPage.Paragraph>
            {t(`${TP}.onceYouRegister`, "Once you've registered, here's the selling process:")}
          </StaticPage.Paragraph>
          <StyledOrderedList>
            <li>
              <Trans
                i18nKey={`${TP}.createASubmission`}
                t={tv.t}
                defaults="Create a submission on <0>sell.flightclub.com</0> for the shoes you want to sell."
                components={[
                  <InlineStyledLink
                    href="https://sell.flightclub.com"
                    rel="noopener noreferrer"
                    target="_blank"
                  />,
                ]}
              />
            </li>
            {isMarketPricing ? (
              <li>
                <Trans
                  i18nKey={`${TP}.onceReceived`}
                  t={tv.t}
                  defaults="Once received, we process and verify your sneakers' authenticity. We then list them across our network of selling channels — including our retail stores, <0>flightclub.com</0> <1>goat.com</1> and the GOAT app."
                  components={[<FCLink href="/" />, <FCLink href="https://goat.com/" />]}
                />
              </li>
            ) : (
              <li>
                <Trans
                  i18nKey={`${TP}.onceReceivedNotMarketing`}
                  t={tv.t}
                  defaults="Once received, we process and verify your sneakers' authenticity. We then list them across our network of selling channels — including our retail stores and <0>flightclub.com</0>."
                  components={[<FCLink href="/" />]}
                />
              </li>
            )}
            <li>
              <Trans
                i18nKey={`${TP}.whenYourShoeSells`}
                t={tv.t}
                defaults="When your shoe sells, you can request a digital payout via PayPal or through a bank transfer on <0>sell.flightclub.com</0>."
                components={[
                  <InlineStyledLink
                    href="https://sell.flightclub.com"
                    rel="noopener noreferrer"
                    target="_blank"
                  />,
                ]}
              />
            </li>
          </StyledOrderedList>
          <UnstyledList>
            <li data-testid="consignLinks">
              <StyledLink
                data-qa={PagesQA.ConsignFaqLink}
                href="https://support.flightclub.com/hc/en-us/sections/360008914071-SELLING"
              >
                {t(`${TP}.faq`, 'FAQ')}
              </StyledLink>
            </li>
            <li data-testid="consignLinks">
              <FCLink qaAttr={PagesQA.ConsignTermsConditionsLink} href="/seller-terms">
                {t(`${TP}.termsOfUse`, 'Terms of Use')}
              </FCLink>
            </li>
            <li data-testid="consignLinks">
              <StyledLink
                data-qa={PagesQA.ConsignContactUsLink}
                href="mailto:support@flightclub.com"
              >
                {t(`${TP}.contactUs`, 'Contact Us')}
              </StyledLink>
            </li>
          </UnstyledList>
        </StaticPage>
      )}
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { req, res, locale } = context

  try {
    const { serverRuntimeConfig } = getConfig()
    const { mosaicRaffleNamespace } = serverRuntimeConfig
    const mosaicClient = createMosaicClient(req, locale)
    const { data } = await mosaicClient.get(`/api/v1/mosaic/${mosaicRaffleNamespace}/static-images`)

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        staticImages: data.images,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        staticImages: {},
      },
    }
  }
}

const InlineStyledLink = styled.a`
  color: inherit;
  text-decoration: none;
  font: inherit;
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`
const StyledLink = styled.a`
  color: inherit;
  text-decoration: underline;
  font: inherit;
  font-weight: 500;

  &:hover {
    filter: brightness(60%);
  }
`
const UnstyledList = styled.ul`
  ${fonts.BODY_TEXT}
  list-style-type: none;
  padding-left: 0;
`
const StyledOrderedList = styled.ol`
  ${fonts.BODY_TEXT}
  line-height: 16px;
`
const ButtonWrapper = styled.div`
  display: block;
  margin: 40px 0 0;
  text-align: left;
`
const ButtonLink = styled(Button)`
  height: fit-content;
  display: block;
  line-height: 16px;
  padding: 16px;
  width: 100%;
  ${media.large`
  width: 422px;
  `}
`

export default Consign
